import { Box, Modal, makeStyles, TextField, InputAdornment, Grid, FormControlLabel, Switch, Dialog, Typography, Button } from '@material-ui/core'
import { Search as SearchIcon } from '@material-ui/icons'
import algoliasearch from 'algoliasearch'
import { InstantSearch, useConfigure, useInstantSearch, usePagination, useSearchBox } from 'react-instantsearch'
import { BeatLoader } from 'react-spinners'
import Pagination from '../../elasticSearch/pagination/Pagination'
import Empty from '../../mui/Empty'
import { MAIN_STAFFERS_INDEX } from '../../../constants/algolia'
import { algoliaInfo } from '../../../config'
import { AlgoliaStafferType } from '../../../../../src/algolia/staffer'
import { OptionType } from '../CreateEditJob'
import { FC, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import FeedIcon from '@mui/icons-material/Feed';
import CloseIcon from '@mui/icons-material/Close';
import styles from './OfferJobModal.module.css'
import { IconButton } from '@mui/material'
import Avatar from '../../avatar/Avatar'
import { GeoPoint, Timestamp } from '../../../../../src/types/firebase'
import AddStaffer from '../AddStaffer'
import { JobType } from '../../../../../src/types/jobs'
import { BusinessType } from '../../../../../src/types/business'
import ButtonWithDisabledTooltip from '../../ButtonWithDisabledTooltip'
import ModalHeader from '../../mui/ModalHeader'

const HITS_PER_PAGE = 5

const searchClient = algoliasearch(algoliaInfo.APP_ID, algoliaInfo.SEARCH_KEY)

const useStyles = makeStyles((theme) => ({
  modal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    maxWidth: '90vw',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  title: {
    marginBottom: theme.spacing(3),
  },
  searchBar: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
}))

type Props = {
  isOpen: boolean
  jobType: string
  business: BusinessType
  job?: JobType
  createdJob: {
    businessId: string
    description: string
    isFreelanceJob: boolean
    location: GeoPoint | null
    timeEnd: Timestamp
    timeStart: Timestamp
    employmentReason: string
    presentStaffers: Array<string>
    salaryCurrency: string
    salaryHourly: string | number
    jobType: string
    options: Array<string>
  }
  disabled: boolean
  disabledReason: string
  invalidFormReasons: { [reason: string]: string }
  onClose: () => void
  onBack: () => void
  selectOption: (option: OptionType) => void
  removeOption: (removeId: string) => void
  invites: OptionType[]
  limitedToInvitedUntil: boolean | Timestamp
  onPressSaveJob: () => void
  toggleLimitedToInvitedUntil: () => void
  recommendedWageDialog: 'open' | 'closed' | 'dismissed'
  setDialogClosed: () => void
  setDialogDismissed: () => void
}

type StafferSearchProps = {
  jobType: string
  selectOption: (option: OptionType) => void
  removeOption: (removeId: string) => void
  invites: OptionType[]
}

const SearchBar = () => {
  const { refine } = useSearchBox()
  const classes = useStyles()

  return (
    <TextField
      className={classes.searchBar}
      placeholder="Search by name or phone number..."
      variant="outlined"
      onChange={(e) => refine(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  )
}

const StafferSearch: FC<StafferSearchProps> = ({ jobType, selectOption, removeOption, invites }) => {
  const { status, results } = useInstantSearch()

  const { refine: setPage, currentRefinement: currentPage, nbPages } = usePagination()

  useConfigure({
    // @ts-ignore
    hitsPerPage: HITS_PER_PAGE,
    filters: `positions:"${jobType}"`,
  })

  const isStaffersListing = results.index.startsWith(MAIN_STAFFERS_INDEX)

  return (
    <Box style={{ overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <SearchBar />
      <Box display="flex" justifyContent="space-between">
        <div style={{ display: 'flex', justifyContent: 'space-between', flex: 1 }}>
          <div>{status === 'loading' && <BeatLoader />}</div>
          <Box display="flex" alignItems="center">
            {/* pagination */}
            {results.nbHits} results &nbsp;
            <Pagination onPageChange={setPage} totalPages={nbPages} currentPage={currentPage} compactVersion />
          </Box>
        </div>
      </Box>

      {/* render staffers results */}
      {isStaffersListing && (
        <div className={styles.stafferListing}>
          {results.hits.map((hit: AlgoliaStafferType) => (
            <div key={hit.objectID} className={styles.stafferItem}>
            <div className={styles.stafferItemContent}>
              {/* <img src={hit.photoUrl ?? DefaultAvatar} alt={'staffer'} style={{ width: 100, height: 100, borderRadius: 50 }} /> */}
              <span className={styles.stafferItemName}>
                {hit.nameFirst} {hit.nameLast}
              </span>
              <span>
                {hit.phone}
              </span>
            </div>
            {invites.find((i) => i.id === hit.objectID) ? (
              <Button variant="contained" color="secondary" onClick={() => removeOption(hit.objectID)}>Remove</Button>
            ) : (
              <Button variant="contained" color="primary" onClick={() => selectOption({ id: hit.objectID, label: `${hit.nameFirst} ${hit.nameLast}`, photoUrl: hit.photoUrl, phone: hit.phone })}>
              Invite
              </Button>
            )}
            </div>
          ))}
        </div>
      )}
      {results.hits.length === 0 && <Empty label="No matching results" />}
    </Box>
  )
}

const OfferJobModal: FC<Props> = ({
  isOpen,
  onClose,
  jobType,
  business,
  job,
  createdJob,
  selectOption,
  removeOption,
  invites,
  onBack,
  toggleLimitedToInvitedUntil,
  limitedToInvitedUntil,
  onPressSaveJob,
  invalidFormReasons,
  disabledReason,
  disabled,
  recommendedWageDialog,
  setDialogClosed,
  setDialogDismissed,
}) => {
  const [activeStafferId, setActiveStafferId] = useState<string | null>(null)
  const [acceptedWorkAgreements, setAcceptedWorkAgreements] = useState<boolean>(false)


  const openContractPreview = (stafferId: string) => {
    setActiveStafferId(stafferId)
  }

  const hideContractModal = () => {
    setActiveStafferId(null)
  }

  const toggleAcceptedWorkAgreements = () => {
    setAcceptedWorkAgreements(!acceptedWorkAgreements)
  }

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      aria-labelledby="offer-job-modal"
      aria-describedby="modal-for-selecting-staffers"
    >
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <div className={styles.title}>
            <div className={styles.titleLeft}>
              <IconButton onClick={onBack}>
                <ChevronLeftIcon className={styles.icon} />
              </IconButton>
              <span>
                Invite staffers
              </span>
            </div>
            <IconButton onClick={onClose}>
              <CloseIcon className={styles.icon} />
            </IconButton>
          </div>
        </div>
        <div className={styles.modalContent}>
          <InstantSearch searchClient={searchClient} indexName={MAIN_STAFFERS_INDEX}>
            <StafferSearch jobType={jobType} selectOption={selectOption} removeOption={removeOption} invites={invites} />
          </InstantSearch>
          <Grid container spacing={1} style={{ overflow: 'auto', maxHeight: '14rem' }}>
          {invites.map((i) => (
            <Grid item xs={12} sm={6} key={i.id}>
              <div className={styles.invitesItem}>
                <Avatar imageUrl={i.photoUrl} avatarSize="medium" />
                <div className={styles.inviteItemInfo}>
                  <span>{i.label}</span>
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 10 }}>
                    <IconButton onClick={() => openContractPreview(i.id)}>
                      <FeedIcon className={styles.icon} style={{ color: 'black' }} />
                    </IconButton>
                    <IconButton onClick={() => removeOption(i.id)}>
                      <CloseIcon className={styles.icon} style={{ color: 'red' }} />
                    </IconButton>
                  </div>
                </div>
              </div>
            </Grid>
          ))}
          </Grid>
          <div className={styles.togglesContainer}>
          <FormControlLabel
                control={
                  <Switch
                    disabled={typeof limitedToInvitedUntil !== 'boolean' && !!limitedToInvitedUntil}
                    checked={!!limitedToInvitedUntil}
                    onChange={toggleLimitedToInvitedUntil}
                    color="primary"
                  />
                }
                label="Make this job non-exclusive after 6 hours"
              />
              <FormControlLabel
                control={
                  <Switch
                    disabled={!(invites && invites.length)}
                    checked={!!acceptedWorkAgreements}
                    onChange={toggleAcceptedWorkAgreements}
                    color="primary"
                  />
                }
                label="I accept the work agreement(s)*"
              />
          </div>
          <Modal
          open={!!activeStafferId}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClose={hideContractModal}
        >
          <div className={styles.modal}>
            <AddStaffer
              business={business}
              isContractPreview
              job={job || createdJob}
              onAcceptStaffer={hideContractModal}
              onClose={hideContractModal}
              stafferId={activeStafferId}
            />
          </div>
        </Modal>
        <Dialog open={recommendedWageDialog === 'open'} onClose={setDialogClosed}>
          <ModalHeader close={setDialogClosed}>Recommended wage</ModalHeader>
          <Box p={2}>
            <Typography>Hey there! 👋</Typography>
            <br />
            <Typography>
              The wage you&apos;ve set for this same-day job posting is below the recommended 220 NOK per hour. 📉 To
              boost your chances of success, consider offering a higher wage. 🚀
            </Typography>{' '}
            <br />
            <Typography>What would you like to do?</Typography>
            <Box py={1} display="flex" alignItems="center">
              <Button variant="contained" color="primary" onClick={setDialogClosed}>
                Go back
              </Button>
              <Box ml={1}>
                <Button onClick={setDialogDismissed} variant="contained">
                  Post anyway
                </Button>
              </Box>
            </Box>
          </Box>
        </Dialog>
        </div>
        <div className={styles.modalFooter}>
          <Button id="go-back-btn" onClick={onBack} variant="contained">
            Go back
          </Button>
          <ButtonWithDisabledTooltip
            onClick={onPressSaveJob}
            variant="contained"
            color="primary"
            reasons={{
              ...invalidFormReasons,
              ...(acceptedWorkAgreements
                ? {}
                : { disagreed: 'You need to accept the work agreement(s) before offering the job' }),
            }}
            reason={acceptedWorkAgreements ? disabledReason : 'disagreed'}
            disabled={disabled || !(invites && invites.length) || !acceptedWorkAgreements}
            >
              {job && job.id ? 'Save job with invites' : 'Post job with invites'}
            </ButtonWithDisabledTooltip>
        </div>
      </div>
    </Modal>
  )
}

export default OfferJobModal
